import { useEffect, useRef, useState } from 'react'

/**
 * Kinetische Typografie: der Text baut sich Zeichen für Zeichen auf, während
 * die noch nicht gesetzten Stellen durch Glyphen laufen.
 *
 * Warum das hier passt und nicht bloss Effekt ist: die Wortmarke und die
 * Produktnamen dieser Marke sind Versal und technisch gesetzt. Ein Aufbau, der
 * aussieht wie eine Anzeige, die sich einpegelt, verlängert genau diese Geste.
 *
 * Drei Regeln, damit es nicht billig wird:
 *
 *  · Die Länge bleibt von der ersten Bildfolge an konstant. Sonst springt das
 *    Layout, und das ist bei einer Überschrift sofort sichtbar.
 *  · Leerzeichen und Bindestriche laufen NICHT mit. Wortgrenzen bleiben stehen,
 *    der Text bleibt die ganze Zeit als Text lesbar statt als Rauschen.
 *  · Bei `prefers-reduced-motion` steht der fertige Text sofort da. Laufende
 *    Glyphen sind genau die Art Bewegung, die dabei stört.
 */

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&/<>[]{}*+='

/** Zeichen, die nie ersetzt werden — sie halten die Wortgrenzen. */
function isFixed(char: string): boolean {
  return char === ' ' || char === ' ' || char === '-' || char === '·'
}

export interface ScrambleProps {
  text: string
  /** Zeichen je Sekunde, die sich festsetzen. */
  speed?: number
  /** Verzögerung vor dem Start, in Millisekunden. */
  delay?: number
  className?: string
  as?: 'span' | 'h1' | 'h2' | 'p'
}

export function Scramble({
  text,
  speed = 26,
  delay = 0,
  className,
  as: Tag = 'span',
}: ScrambleProps) {
  const [output, setOutput] = useState(text)
  const frame = useRef(0)

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setOutput(text)
      return
    }

    let raf = 0
    let timer = 0
    const start = () => {
      const began = performance.now()

      const tick = (now: number) => {
        const settled = ((now - began) / 1000) * speed

        const next = text
          .split('')
          .map((char, i) => {
            if (isFixed(char) || i < settled) return char
            // Ein Zeichen kurz vor seiner Festsetzung wechselt schneller —
            // das erzeugt die Anmutung des Einrastens.
            return GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
          })
          .join('')

        setOutput(next)

        if (settled < text.length) {
          raf = requestAnimationFrame(tick)
        } else {
          setOutput(text)
        }
      }

      raf = requestAnimationFrame(tick)
    }

    // Vor dem Start steht bereits die volle Länge — sonst springt das Layout
    // beim ersten Bild.
    setOutput(
      text
        .split('')
        .map((c) => (isFixed(c) ? c : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]))
        .join(''),
    )

    if (delay > 0) timer = window.setTimeout(start, delay)
    else start()

    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(timer)
      frame.current = 0
    }
  }, [text, speed, delay])

  // Der wahre Text bleibt für Screenreader stehen; die laufende Fassung ist
  // nur Bild. Ohne das läse ein Screenreader Buchstabensalat vor.
  return (
    <Tag className={className}>
      <span aria-hidden="true">{output}</span>
      <span className="visually-hidden">{text}</span>
    </Tag>
  )
}
